import {
  HHButton,
  HHStack,
  HHToggleButton,
  HHToggleButtonGroup,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../constants/strings/routes';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useVbdPageData } from '../../hooks/discount-hooks';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  ScopeableType,
  useCreateDiscountMutation,
  useUpdateDiscountMutation,
} from '../../types';
import { ActiveStatus } from '../../utils/contract-utils';
import {
  DiscountType,
  IDiscount,
  toEditableScopes,
} from '../../utils/discount-utils';
import { VbdForm } from './vbd-form/form';
import { ParsedVbdFormValues, VbdFormValues } from './vbd-form/form-constants';
import VbdList from './vbd-list';

enum VbdStatusFilter {
  All = 'all',
  Active = 'active',
}

export const buildInitialValues = (vbd: IDiscount): VbdFormValues => {
  // ACCOUNTS-AND-PLAN-MIGRATION: partnershipId/clientId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const hasPartnership = vbd.partnershipId != null;
  const hasClient = vbd.clientId != null;
  // Mixed VBDs (both partnershipId and clientId set) are no longer creatable;
  // treat them as partnership-level when opening the edit form.
  const level = hasClient && !hasPartnership ? 'client' : 'partnership';

  const tiers = vbd.discountDetails
    .slice()
    .sort((a, b) => a.tier - b.tier)
    .map(detail => {
      const ms = detail.milestoneDiscount as Record<string, number>;
      return {
        upperBound: detail.subscriptionTier[1] ?? null,
        discount: detail.discount,
        c1: Math.round((ms?.c1 ?? 0) * 100),
        c2: Math.round((ms?.c2 ?? 0) * 100),
        c3: Math.round((ms?.c3 ?? 0) * 100),
      };
    });

  const existingScopes = toEditableScopes(vbd.scopes);

  return {
    level,
    partnershipId: vbd.partnershipId ?? null,
    clientId: vbd.clientId ?? null,
    scopeableType:
      existingScopes.length > 0
        ? existingScopes[0].scopeableType
        : ScopeableType.Global,
    scopes: existingScopes,
    startDate: vbd.startDate,
    endDate: vbd.endDate ?? null,
    suppressEmptyCharges: vbd.suppressEmptyCharges,
    tiers:
      tiers.length > 0
        ? tiers
        : [{ upperBound: null, discount: null, c1: null, c2: null, c3: null }],
  };
};

const VbdGlobalView = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const navigate = useNavigate();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const [statusFilter, setStatusFilter] = useState(VbdStatusFilter.All);

  const {
    vbds,
    vbdsLoading,
    refetchVbds,
    partnerships,
    partnershipsLoading,
    clients,
    clientsLoading,
    partnershipMap,
    clientMap,
    packages,
    packagesLoading,
  } = useVbdPageData();

  const [createDiscount] = useCreateDiscountMutation();
  const [updateDiscount] = useUpdateDiscountMutation();

  const filteredVbds =
    statusFilter === VbdStatusFilter.Active
      ? vbds.filter(d => d.status === ActiveStatus.Active)
      : vbds;

  const openDrawerForCreate = useCallback((): void => {
    const handleCreate = async (values: ParsedVbdFormValues): Promise<void> => {
      try {
        await createDiscount({
          variables: {
            discountInput: {
              discountType: DiscountType.volumeBasedDiscounts,
              // ACCOUNTS-AND-PLAN-MIGRATION: partnershipId/clientId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
              partnershipId: values.partnershipId,
              clientId: values.clientId,
              scopes: values.scopes.map(s => ({
                scopeableType: s.scopeableType,
                scopeableId: s.scopeableId ?? 0,
              })),
              startDate: values.startDate,
              endDate: values.endDate,
              suppressEmptyCharges: values.suppressEmptyCharges,
              vbdTiers: values.vbdTiers,
            },
          },
        });
        refetchVbds();
        setSnackbarState('VBD created successfully', 'success', 3000);
        onClose();
      } catch {
        setSnackbarState('Error creating VBD', 'error', 3000);
      }
    };

    setDrawerContent(
      <HHStack padding={4}>
        <VbdForm
          onSubmit={handleCreate}
          vbds={vbds}
          partnerships={partnerships}
          partnershipsLoading={partnershipsLoading}
          clients={clients}
          clientsLoading={clientsLoading}
          packages={packages}
        />
      </HHStack>,
      <HHTypography hhVariant="h6">Add Volume Based Discount</HHTypography>,
      '50%',
      'temporary',
    );
  }, [
    setDrawerContent,
    createDiscount,
    refetchVbds,
    setSnackbarState,
    onClose,
    vbds,
    partnerships,
    partnershipsLoading,
    clients,
    clientsLoading,
    packages,
  ]);

  const handleEditClick = useCallback(
    (discountDefinitionId: number): void => {
      const vbd = vbds.find(
        d => d.discountDefinitionId === discountDefinitionId,
      );
      if (!vbd) return;

      const initialValues = buildInitialValues(vbd);

      const handleUpdate = async (
        values: ParsedVbdFormValues,
      ): Promise<void> => {
        try {
          await updateDiscount({
            variables: {
              discountInput: {
                discountDefinitionId,
                scopes: values.scopes.map(s => ({
                  scopeableType: s.scopeableType,
                  scopeableId: s.scopeableId ?? 0,
                })),
                startDate: values.startDate ?? undefined,
                endDate: values.endDate,
                suppressEmptyCharges: values.suppressEmptyCharges,
                vbdTiers: values.vbdTiers,
              },
            },
          });
          refetchVbds();
          setSnackbarState('VBD updated successfully', 'success', 3000);
          onClose();
        } catch {
          setSnackbarState('Error updating VBD', 'error', 3000);
        }
      };

      setDrawerContent(
        <HHStack padding={4}>
          <VbdForm
            onSubmit={handleUpdate}
            initialValues={initialValues}
            editDefinitionId={discountDefinitionId}
            vbds={vbds}
            partnerships={partnerships}
            partnershipsLoading={partnershipsLoading}
            clients={clients}
            clientsLoading={clientsLoading}
            packages={packages}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">Edit Volume Based Discount</HHTypography>,
        '50%',
        'temporary',
      );
    },
    [
      vbds,
      setDrawerContent,
      updateDiscount,
      refetchVbds,
      setSnackbarState,
      onClose,
      partnerships,
      partnershipsLoading,
      clients,
      clientsLoading,
      packages,
    ],
  );

  return (
    <HHStack flex={1} direction="column" minHeight="0">
      <HHStack
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHStack direction="row" alignItems="center" spacing={2}>
          <HHTypography hhVariant="section-title">
            Volume Based Discounts
          </HHTypography>
          <HHToggleButtonGroup
            exclusive
            value={statusFilter}
            onChange={(_, newValue): void => {
              if (newValue !== null) {
                setStatusFilter(newValue as VbdStatusFilter);
              }
            }}
            size="small"
            aria-label="filter VBDs by status"
            sx={{ height: '40px' }}
          >
            <HHToggleButton
              hhVariant="variant-bypass"
              value={VbdStatusFilter.All}
              sx={{ borderRadius: '4px 0 0 4px', textTransform: 'none' }}
            >
              View All
            </HHToggleButton>
            <HHToggleButton
              hhVariant="variant-bypass"
              value={VbdStatusFilter.Active}
              sx={{ borderRadius: '0 4px 4px 0', textTransform: 'none' }}
            >
              View Active
            </HHToggleButton>
          </HHToggleButtonGroup>
        </HHStack>
        <HHStack direction="row" spacing={2}>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            size="medium"
            startIcon={<CalculateOutlined />}
            onClick={(): void => {
              navigate(
                `${routes.billing.home}/${routes.billing.discountCalculator}`,
              );
            }}
          >
            Discount Calculator
          </HHButton>
          {canWrite && (
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              onClick={openDrawerForCreate}
            >
              Add VBD
            </HHButton>
          )}
        </HHStack>
      </HHStack>
      <HHStack
        flex={1}
        direction="column"
        spacing={4}
        sx={{ overflowY: 'auto', padding: 6, paddingTop: 0 }}
      >
        <VbdList
          vbdsData={filteredVbds}
          loading={
            vbdsLoading ||
            packagesLoading ||
            partnershipsLoading ||
            clientsLoading
          }
          availablePackages={packages}
          partnershipMap={partnershipMap}
          clientMap={clientMap}
          onEdit={canWrite ? handleEditClick : undefined}
        />
      </HHStack>
    </HHStack>
  );
};

export default VbdGlobalView;
